import React, { useMemo } from 'react';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import EntityName from 'components/Globals/EntityName';
import { usePaywallContext } from 'components/Paywall';

import { TP, DATE_FORMATS } from 'constants/index';

import { createDate, createDatePath } from 'utils/date';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import useNavigate from 'utils/hooks/useNavigate';
import { getProductionDatesGroupedByVenues } from 'utils/productions';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './PublicVenuesAndDates.module.scss';

const DatesList = ({ dates, isCancelled, linkProps, venueId, trackingData = {} }) => {
  const { isMobile } = useDeviceTypeLayouts();

  return (
    <div className={classes.datesList}>
      {Object.keys(dates).map((monthYear, datesIndex) => {
        const [month, year] = monthYear.split(' ');
        const fullMonthName = createDate(`${year}-${month}`).format('MMMM');
        return (
          <div className={classes.dateMonth} key={`${datesIndex}-${monthYear}-${venueId}`}>
            <Typography
              className={classes.month}
              weight={isMobile ? 'medium' : 'regular'}
              color="secondary"
              {...(isCancelled && { strikethrough: true })}
              aria-label={`${year}-${month}`}
            >
              <time dateTime={`${fullMonthName} ${year}`}>
                {month}
                {isMobile ? `'${year.slice(2)}` : ''}
              </time>
            </Typography>
            <ol className={classes.dates}>
              {dates[monthYear]?.map((date, i) => {
                const performanceLinkProps = {
                  ...linkProps?.getSubPath({
                    path: createDatePath(date?.startDate),
                  }),
                };

                return (
                  <li className="p-performance" key={`${date.id}-${venueId}-${i}`}>
                    <Typography variant="h6" color="secondary">
                      <span className={classes.visuallyHidden}>
                        <time dateTime={createDate(date?.startDate)?.format(DATE_FORMATS.FULL_MONTH)} key={i}>
                          {createDate(date.startDate).format(DATE_FORMATS.FULL_MONTH)}{' '}
                        </time>
                      </span>
                      <LinkButton
                        variant="native"
                        isLink
                        {...performanceLinkProps}
                        preventDefault
                        trackingData={trackingData}
                      >
                        <Typography
                          className={classes.date}
                          color="secondary"
                          {...(isCancelled && { strikethrough: true })}
                        >
                          <time dateTime={createDate(date?.startDate)?.format(DATE_FORMATS.FULL_DATE)} key={i}>
                            {createDate(date?.startDate)?.format(DATE_FORMATS.DATE)}
                          </time>
                        </Typography>
                      </LinkButton>
                    </Typography>
                  </li>
                );
              })}
            </ol>
          </div>
        );
      })}
    </div>
  );
};

const VenueContent = ({ venue, isCancelled, venueLinkProps, trackingData, isArchived }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { setIsPaywallExpanded } = usePaywallContext();

  return (
    <div
      className={classes.venueContent}
      {...(isArchived && {
        onClick: e => {
          e.stopPropagation();
          e.preventDefault();
          setIsPaywallExpanded(true);
        },
      })}
    >
      <div>
        {venue?.id && (
          <SpriteIcon
            icon="location_on_black"
            size={12}
            className={classes.locationIcon}
            ariaLabel={t(`${TP}.m_ASKLOCATION`)}
          />
        )}
      </div>
      <div className={classes.venueContent__right}>
        {venue?.id ? (
          <Typography
            size={12}
            color="secondary"
            variant="h5"
            className={classes.name}
            {...(isCancelled && { strikethrough: true })}
            blur={venue?.id === -1}
            truncate
          >
            <LinkButton
              variant="text"
              preventDefault
              {...venueLinkProps}
              isLink={venue?.id !== -1}
              styles={{
                root: classes.venueLink,
              }}
              trackingData={trackingData}
            >
              <EntityName entityType={ENTITY_TYPES.ORGANIZATION} entity={venue} trackingData={trackingData} isRaw />
            </LinkButton>
          </Typography>
        ) : (
          <Typography
            size={12}
            weight="medium"
            {...(isCancelled && { strikethrough: true })}
            className={classes.notAnnounced}
          >
            {t(`${TP}.FN_VENUE_NOT_ANNOUNCED`)}
          </Typography>
        )}
        <Typography variant="h5" className={classes.venue}>
          {venue?.city?.name && (
            <Typography
              size={12}
              weight="medium"
              color="secondary"
              {...(isCancelled && { strikethrough: true })}
              blur={venue?.id === -1}
            >
              {venue?.city?.name}
              {', '}
            </Typography>
          )}
          <Typography size={12} color="secondary" {...(isCancelled && { strikethrough: true })} blur={venue?.id === -1}>
            {venue?.country?.name}
          </Typography>
        </Typography>
      </div>
    </div>
  );
};

const PublicVenuesAndDates = ({ production, trackingData, linkProps }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const isArchived = production?.isArchived;
  const navigate = useNavigate();
  const { isCancelled } = production || {};
  const venuesDates = useMemo(() => {
    if (!production) {
      return undefined;
    }

    return getProductionDatesGroupedByVenues({ production, dateFormat: DATE_FORMATS.MONTH_YEAR });
  }, [production]);

  if (!venuesDates?.length) {
    return null;
  }

  return (
    <ul className={classes.venueItems}>
      {venuesDates?.slice(0, 2)?.map(({ venue, dates }, index) => {
        const venueLinkProps = navigate.getLinkProps({
          entity: venuesDates?.[0]?.venue,
          entityType: ENTITY_TYPES.ORGANIZATION,
        });

        const isSameCity = venue?.city?.id === venuesDates?.[index - 1]?.venue?.city?.id;

        return (
          <React.Fragment key={`${venue?.id}-${venue?.stage?.id}-${index}`}>
            <li className={classes.venueItem}>
              <VenueContent
                venue={venue}
                isCancelled={isCancelled}
                showHeader={!isSameCity}
                venueLinkProps={venueLinkProps}
                trackingData={trackingData}
                isArchived={isArchived}
              />
              <div className={classes.venueItem__venueAndDates}>
                <DatesList
                  venueId={venue?.id}
                  dates={dates}
                  isCancelled={isCancelled}
                  linkProps={linkProps}
                  trackingData={trackingData}
                />
              </div>
            </li>
            {index === 1 && venuesDates?.length > 2 && (
              <Typography size={12} color="secondary" italic className={classes.moreVenues}>
                {`+${venuesDates.length - 2} ${venuesDates.length - 2 > 1 ? t(`${TP}.m_VENUES`) : t(`${TP}.m_VENUE`)}`}
                <SpriteIcon icon="chevron_right" size={16} className={classes.chevronIcon} />
              </Typography>
            )}
          </React.Fragment>
        );
      })}
    </ul>
  );
};

export default PublicVenuesAndDates;
